import ajax from '../../helpers/ajax'
import { objectKeysToSnakeCase } from '../../helpers/transform-object-keys'
import { OneClickFromCrmStatus } from './one-click-from-crm-statuses'

const PREFILL_FORMS_STATE_ALLOWED = 'allowed'
const PREFILL_FORMS_STATE_FORBIDDEN = 'forbidden'

export default {
  fetchGlobalSubscriptionDataFromCrm({ state, commit }) {
    ajax.get({
      url: state.globalSubscriptionCheck.url,
      throwOnFailure: true,
      successHandler: (resp) => {
        if (resp.subscribed) {
          commit('setCrmSubscribed')
        }
      }
    }).catch(() => {
      // If the network call to get the data fails, we want to just handle that situation,
      // rather than showing a "network call failed" message to the user.
      commit('setGlobalSubscriptionCheckFromCrmFailed')
    })
  },

  fetchOneClickDataFromCrm({ state, commit, dispatch }) {
    ajax.get({
      url: state.crmOneClickUrl,
      camelCaseResponse: false,
      throwOnFailure: true,
      successHandler: (resp) => {
        // We got data from the CRM!
        dispatch('handleIfAlreadyTakenAction', resp).then((alreadyTakenAction) => {
          if (!alreadyTakenAction) {
            // We've enough information from the CRM to potentially do one-click signing, we first need to check if member
            // has given consent for prefilling forms though, and ask for it in case they haven't decided yet.
            if (resp.requires_consent_for_prefill) {
              if (state.askForPrefillFormsConsentIfNeeded) {
                state.oneClickFromCrmStatus = OneClickFromCrmStatus.REQUIRES_PREFILL_FORMS_CONSENT
                // Store the one-click data so that if the member gives consent for prefilling the form, we can use it.
                commit('storeOneClickDataFromCrm', resp)
                commit('askForPrefillFormsConsent')
              } else {
                // If we need to ask for prefill forms consent, but the CRM doesn't support it we can't proceed with one-click.
                // This is unexpected and shouldn't happen in normal usage, but we don't raise an error because there's no
                // client-side error reporting for these pages.
                commit('oneClickFromCrmFailed')
              }
            } else {
              // Store the one-click data to make it available for the oneClickFromCrmSucceeded action
              commit('storeOneClickDataFromCrm', resp)
              dispatch('oneClickFromCrmSucceeded', resp)
            }
          } else {
            state.oneClickFromCrmStatus = OneClickFromCrmStatus.ALREADY_TOOK_ACTION
          }
        })
      }
    }).catch(() => {
      // If the network call to get the data fails or tokyo returned 404 because it couldn't find the member,
      // we handle that situation rather than showing a "network call failed" message to the user.
      commit('oneClickFromCrmFailed')
    })
  },

  oneClickFromCrmSucceeded({ state, commit, dispatch }) {
    const oneClickDataFromCrm = state.oneClickDataFromCrm
    // Use the one-click signing UX! We got enough information, member allows prefilling forms and the action-taker hasn't already taken action on this campaign.
    commit('activateOneClickFromCrm', oneClickDataFromCrm)
    commit('applyOneClickFieldMappings', oneClickDataFromCrm)

    dispatch('trackSawOneClick')

    state.oneClickFromCrmStatus = OneClickFromCrmStatus.SUCCEEDED

    if (oneClickDataFromCrm.subscribed) {
      commit('setCrmSubscribed')
    }
  },

  recordOptInForPrefillForms({ dispatch }) {
    dispatch('recordPrefillFormsState', PREFILL_FORMS_STATE_ALLOWED)
  },

  recordOptOutForPrefillForms({ dispatch }) {
    dispatch('recordPrefillFormsState', PREFILL_FORMS_STATE_FORBIDDEN)
  },

  recordPrefillFormsState({ state, commit, dispatch }, prefillFormsState) {
    const data = {
      prefill_forms_state: prefillFormsState,
      campaign_slug: state.prefillFormsConsent.campaignSlug,
      ...state.prefillFormsConsent.utmParams
    }

    ajax.post({
      url: state.prefillFormsConsent.updatePrefillFormsPath,
      data,
      successHandler: () => {
        if (prefillFormsState === PREFILL_FORMS_STATE_ALLOWED) {
          dispatch('oneClickFromCrmSucceeded')
        } else {
          // Member opted out of prefilling forms, so we proceed without one-click
          commit('oneClickFromCrmFailed')
        }
      }
    })
  }
}

export function getNewActionPayload(state, actionType, suppressEmailOptInField, {friendlyCaptchaSolution, hcaptchaResponse, recaptchaToken, turnstileToken }) {
  const data = {
    [actionType]: {
      ...objectKeysToSnakeCase(state.action),
      ...state.extraParams
    },
    retry_count: state.retryCount
  }

  // Reset the additional fields attribute that we want to send without transforming to snake case
  data[actionType].additional_fields = state.action.additionalFields

  if (suppressEmailOptInField) {
    delete data[actionType].join_organisation
    delete data[actionType].email_opt_in_type_id
  }

  if (friendlyCaptchaSolution) {
    data.friendly_captcha_solution = friendlyCaptchaSolution
  }

  if (hcaptchaResponse) {
    data.hcaptcha_response = hcaptchaResponse
  }

  if (recaptchaToken) {
    data.recaptcha_token = recaptchaToken
  }

  if (turnstileToken) {
    data.turnstile_token = turnstileToken
  }

  return data
}
